import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ program_id: "", title: "", body: "", unit_id: "", image_url: "" });
  const canManage = user.role === "admin" || user.role === "supervisor" || user.role === "teacher";

  const load = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); if (canManage) { api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}); api.get("/units").then((r) => setUnits(r.data)).catch(() => {}); } }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    try { await api.post("/announcements", { ...form, program_id: form.program_id || null, unit_id: form.unit_id || null }); toast.success("تم نشر الإعلان"); setOpen(false); setForm({ program_id: "", title: "", body: "", unit_id: "", image_url: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("حذف الإعلان؟")) return; try { await api.delete(`/announcements/${id}`); toast.success("تم الحذف"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const rsvp = async (id, attending) => { try { await api.post(`/announcements/${id}/rsvp`, { attending }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (items === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="الإعلانات" subtitle="إعلانات البرامج للطلاب والمدرسين"
        action={canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-ann-btn" className="rounded-xl bg-navy hover:bg-navy-light"><Plus className="me-2 h-4 w-4" /> إعلان جديد</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading text-navy">نشر إعلان</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div><Label>البرنامج (اختياري — اتركه فارغًا لإعلان عام)</Label>
                  <Select value={form.program_id} onValueChange={set("program_id")}><SelectTrigger data-testid="ann-program" className="mt-1.5 rounded-lg"><SelectValue placeholder="عام لجميع الطلاب" /></SelectTrigger>
                    <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>العنوان</Label><Input data-testid="ann-title" value={form.title} onChange={(e) => set("title")(e.target.value)} className="mt-1.5 rounded-lg" /></div>
                <div><Label>المحتوى</Label><Textarea value={form.body} onChange={(e) => set("body")(e.target.value)} rows={4} className="mt-1.5 rounded-lg" /></div>
                <div><Label>رابط صورة الإعلان (اختياري)</Label><Input data-testid="ann-image" value={form.image_url} onChange={(e) => set("image_url")(e.target.value)} placeholder="https://..." className="mt-1.5 rounded-lg" /></div>
                <div><Label>الوحدة السكنية المستهدفة (اختياري — تظهر بأولوية لسكانها)</Label>
                  <Select value={form.unit_id} onValueChange={set("unit_id")}><SelectTrigger data-testid="ann-unit" className="mt-1.5 rounded-lg"><SelectValue placeholder="بدون تحديد" /></SelectTrigger>
                    <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button data-testid="ann-save" onClick={submit} disabled={!form.title} className="rounded-xl bg-navy hover:bg-navy-light">نشر</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      {items.length === 0 ? <EmptyState icon={Megaphone} title="لا توجد إعلانات" /> : (
        <div className="stagger space-y-4">
          {items.map((a) => (
            <div key={a.id} data-testid={`ann-${a.id}`} className={`rounded-2xl border bg-card p-5 soft-shadow card-hover ${a.is_my_unit ? "border-gold/50 ring-1 ring-gold/30" : "border-slate-100"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold-dark"><Megaphone className="h-5 w-5" /></div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{a.program_name}</span>
                      {a.unit_name && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{a.unit_name}</span>}
                      {a.is_my_unit && <span data-testid="my-unit-badge" className="rounded-full bg-gold/20 px-2 py-0.5 text-xs font-semibold text-gold-dark">يخص وحدتك السكنية</span>}
                    </div>
                    <h3 className="mt-1.5 font-heading text-lg font-bold text-navy">{a.title}</h3>
                    {a.image_url && <img src={a.image_url} alt={a.title} className="mt-2 max-h-56 w-full rounded-xl object-cover" />}
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{a.body}</p>
                    <p className="mt-2 text-xs text-slate-300">{new Date(a.created_at).toLocaleString("ar")}</p>
                    {user.role === "student" && (
                      <div className="mt-3 flex gap-2" data-testid={`rsvp-${a.id}`}>
                        <Button size="sm" data-testid={`rsvp-yes-${a.id}`} onClick={() => rsvp(a.id, true)} className={`rounded-lg ${a.my_rsvp === true ? "bg-greenx text-white hover:bg-greenx-light" : "bg-slate-100 text-slate-600 hover:bg-emerald-50"}`}>سأحضر</Button>
                        <Button size="sm" data-testid={`rsvp-no-${a.id}`} variant="ghost" onClick={() => rsvp(a.id, false)} className={`rounded-lg ${a.my_rsvp === false ? "bg-red-100 text-red-600" : "text-slate-500 hover:bg-red-50"}`}>لن أحضر</Button>
                      </div>
                    )}
                  </div>
                </div>
                {canManage && <Button onClick={() => remove(a.id)} data-testid={`del-ann-${a.id}`} size="sm" variant="ghost" className="text-red-400 hover:bg-red-50 hover:text-red-500 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
